'use client';

import { Activity, Building2, ChevronRight, CreditCard, Database, ExternalLink, FileText, FolderCog, LayoutDashboard, ListChecks, Loader2, Paperclip, RefreshCw, Settings, ShieldCheck, ToggleLeft, ToggleRight, Users, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REALTOR_AUTO_APP_BASE_URL } from '../../../config/ieumConfig';
import { RealtorAutoCommonNoticeDialog } from './RealtorAutoNoticeDialogs';
import { RealtorAutoTemplateManager } from './RealtorAutoTemplateManager';
const EMPTY_ADMIN_NOTICE_DIALOG = {
  open: false,
  title: '알림',
  subtitle: '부동산-AUTO 알림',
  message: '',
  tone: 'info',
  confirmLabel: '확인',
  cancelLabel: ''
};
const PUBLIC_REALTORAUTO_BASE_PATH = (process.env.NEXT_PUBLIC_REALTORAUTO_BASE_PATH || '/realtorauto').replace(/\/+$/u, '') || '/realtorauto';
const DEFAULT_REALTORAUTO_ADMIN_BASE_PATH = `${PUBLIC_REALTORAUTO_BASE_PATH}/admin`;
const DEFAULT_REALTORAUTO_API_BASE_PATH = `${PUBLIC_REALTORAUTO_BASE_PATH}/api/realtorauto`;
const REALTOR_AUTO_ADMIN_SECTIONS = new Set(['dashboard', 'template', 'irospdf', 'payment-macro', 'crawler', 'worker-queue', 'failure-log', 'payments']);
function normalizeAdminSection(value) {
  if (value && REALTOR_AUTO_ADMIN_SECTIONS.has(value)) {
    return value;
  }
  return 'dashboard';
}
const SECTION_ENDPOINTS = {
  irospdf: '/admin/irospdf?limit=80',
  'payment-macro': '/admin/settings',
  crawler: '/admin/crawler-logs?limit=80',
  'worker-queue': '/admin/worker-queue',
  'failure-log': '/admin/failure-logs?limit=80&days=14',
  payments: '/admin/payments?limit=80'
};

async function fetchSectionPayload(section, requestAdmin, force = false) {
  const headers = force ? {
    'X-Ieum-Cache-Mode': 'refresh'
  } : undefined;
  if (section === 'dashboard') {
    const responses = await Promise.all([
      requestAdmin('/admin/settings', { credentials: 'include', cache: 'no-store', headers }),
      requestAdmin('/admin/worker-queue', { credentials: 'include', cache: 'no-store', headers })
    ]);
    const payloads = await Promise.all(responses.map(response => response.json().catch(() => ({}))));
    const failedIndex = responses.findIndex(response => !response.ok);
    if (failedIndex >= 0) return { ok: false, payload: payloads[failedIndex] };
    const [settingsPayload, workerPayload] = payloads;
    return {
      ok: true,
      payload: {
        data: {
          session: { role: 'ieum-admin' },
          settings: record(settingsPayload.data),
          paymentMacroRuntime: record(settingsPayload.paymentMacroRuntime),
          store: record(settingsPayload.store),
          commonAttachments: { documents: [] },
          workerQueue: record(workerPayload.data)
        }
      }
    };
  }
  if (section === 'template') {
    const [settingsResponse, templatesResponse] = await Promise.all([
      requestAdmin('/admin/settings', { credentials: 'include', cache: 'no-store', headers }),
      requestAdmin('/templates', { credentials: 'include', cache: 'no-store', headers })
    ]);
    const [settingsPayload, templatesPayload] = await Promise.all([
      settingsResponse.json().catch(() => ({})),
      templatesResponse.json().catch(() => ({}))
    ]);
    const failedPayload = !settingsResponse.ok ? settingsPayload : !templatesResponse.ok ? templatesPayload : null;
    if (failedPayload) return { ok: false, payload: failedPayload };
    return {
      ok: true,
      payload: {
        data: {
          session: { role: 'ieum-admin' },
          settings: record(settingsPayload.data),
          templates: record(templatesPayload.data),
          paymentMacroRuntime: record(settingsPayload.paymentMacroRuntime),
          store: record(settingsPayload.store || templatesPayload.store)
        }
      }
    };
  }

  const response = await requestAdmin(SECTION_ENDPOINTS[section], {
    credentials: 'include',
    cache: 'no-store',
    headers
  });
  return {
    ok: response.ok,
    payload: await response.json().catch(() => ({}))
  };
}

async function fetchDashboardMetrics(requestAdmin, force = false) {
  const headers = force ? {
    'X-Ieum-Cache-Mode': 'refresh'
  } : undefined;
  const responses = await Promise.all([
    requestAdmin('/admin/irospdf?limit=12', { credentials: 'include', cache: 'no-store', headers }),
    requestAdmin('/admin/crawler-logs?limit=12', { credentials: 'include', cache: 'no-store', headers }),
    requestAdmin('/admin/payments?limit=12', { credentials: 'include', cache: 'no-store', headers })
  ]);
  const payloads = await Promise.all(responses.map(response => response.json().catch(() => ({}))));
  const failedIndex = responses.findIndex(response => !response.ok);
  if (failedIndex >= 0) return { ok: false, payload: payloads[failedIndex] };
  const [irosPdfPayload, crawlerPayload, paymentsPayload] = payloads;
  return {
    ok: true,
    payload: {
      data: {
        irosPdfDocuments: record(irosPdfPayload.data),
        crawlerCollectionLogs: record(crawlerPayload.data),
        paymentRecords: record(paymentsPayload.data)
      }
    }
  };
}
const SECTION_TITLES = {
  dashboard: 'RealtorAuto 운영 현황',
  template: '전체 계약서 기본 템플릿 설정',
  irospdf: 'irospdf 첨부파일 확인',
  'payment-macro': '결제 매크로 설정',
  crawler: '크롤러 수집내역 확인',
  'worker-queue': '워커 대기 현황',
  'failure-log': '실패 로그',
  payments: 'RealtorAuto 결제내역'
};
function sectionOverview(section, payload, current) {
  if (section === 'dashboard' || section === 'template') {
    return record(payload.data);
  }
  const session = current?.session?.role === 'ieum-admin' ? current.session : {
    role: 'ieum-admin'
  };
  if (section === 'irospdf') return {
    session,
    irosPdfDocuments: record(payload.data)
  };
  if (section === 'payment-macro') {
    return {
      session,
      settings: record(payload.data),
      paymentMacroRuntime: record(payload.paymentMacroRuntime),
      store: record(payload.store)
    };
  }
  if (section === 'crawler') return {
    session,
    crawlerCollectionLogs: record(payload.data)
  };
  if (section === 'worker-queue') return {
    session,
    workerQueue: record(payload.data)
  };
  if (section === 'failure-log') return {
    session,
    failureLogs: record(payload.data)
  };
  return {
    session,
    paymentRecords: record(payload.data)
  };
}
function normalizePathPrefix(value, fallback) {
  const next = (value || fallback).trim().replace(/\/+$/u, '');
  return next || fallback.replace(/\/+$/u, '');
}
function adminHref(section, adminBasePath = DEFAULT_REALTORAUTO_ADMIN_BASE_PATH) {
  return section === 'dashboard' ? `${adminBasePath}/` : `${adminBasePath}/${section}/`;
}
function apiHref(path = '', apiBasePath = DEFAULT_REALTORAUTO_API_BASE_PATH) {
  const rawPath = path ? path.startsWith('/') ? path : `/${path}` : '/';
  const queryIndex = rawPath.search(/[?#]/u);
  const pathname = queryIndex >= 0 ? rawPath.slice(0, queryIndex) : rawPath;
  const suffix = queryIndex >= 0 ? rawPath.slice(queryIndex) : '';
  const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return `${apiBasePath}${normalizedPath}${suffix}`;
}

function realtorAutoFileHref(item) {
  const rawPath = text(item?.downloadPath || item?.download_path);
  const fileReferenceId = text(item?.fileReferenceId || item?.file_reference_id);
  const path = rawPath || (fileReferenceId ? `/api/files/${encodeURIComponent(fileReferenceId)}/download/` : '');
  if (!path) return '';
  if (/^https?:\/\//iu.test(path)) return path;
  if (path.startsWith('/realtorauto/')) return `${new URL(REALTOR_AUTO_APP_BASE_URL).origin}${path}`;
  if (path.startsWith('/api/')) return `${REALTOR_AUTO_APP_BASE_URL}${path}`;
  if (path.startsWith('api/')) return `${REALTOR_AUTO_APP_BASE_URL}/${path}`;
  return '';
}
function withAdminBasePath(groups, adminBasePath) {
  return groups.map(group => ({
    ...group,
    items: group.items.map(item => REALTOR_AUTO_ADMIN_SECTIONS.has(item.id) ? {
      ...item,
      href: adminHref(item.id, adminBasePath)
    } : item)
  }));
}
const realtorAutoAdminGroups = [{
  label: '대시보드',
  icon: LayoutDashboard,
  items: [{
    id: 'dashboard',
    label: '운영 현황',
    icon: LayoutDashboard,
    href: adminHref('dashboard')
  }]
}, {
  label: '회원관리',
  icon: Users,
  items: [{
    id: 'partners',
    label: '제휴업체 목록',
    icon: Building2
  }, {
    id: 'realtors',
    label: '공인중개사 목록',
    icon: Users
  }, {
    id: 'members',
    label: '일반회원 목록',
    icon: Users
  }]
}, {
  label: '정산 관리',
  icon: CreditCard,
  items: [{
    id: 'partner-billing',
    label: '제휴업체 미수 정산',
    icon: CreditCard
  }, {
    id: 'billing-history',
    label: '결제내역',
    icon: CreditCard
  }, {
    id: 'broker-fees',
    label: '공인중개사 수수료',
    icon: CreditCard
  }]
}, {
  label: 'realtorauto',
  icon: FolderCog,
  open: true,
  items: [{
    id: 'template',
    label: '전체 계약서 기본 템플릿 설정',
    icon: FileText,
    href: adminHref('template')
  }, {
    id: 'irospdf',
    label: 'irospdf 첨부파일 확인',
    icon: Paperclip,
    href: adminHref('irospdf')
  }, {
    id: 'payment-macro',
    label: '결제 매크로 설정',
    icon: ToggleRight,
    href: adminHref('payment-macro')
  }, {
    id: 'crawler',
    label: '크롤러 수집내역 확인',
    icon: Database,
    href: adminHref('crawler')
  }, {
    id: 'worker-queue',
    label: '워커 대기 현황 및 Q 대기 실시간 열람',
    icon: Activity,
    href: adminHref('worker-queue')
  }, {
    id: 'failure-log',
    label: '실패log',
    icon: XCircle,
    href: adminHref('failure-log')
  }, {
    id: 'payments',
    label: 'realtorauto 결제내역',
    icon: CreditCard,
    href: adminHref('payments')
  }]
}, {
  label: '설정',
  icon: Settings,
  items: [{
    id: 'admin-accounts',
    label: '관리자 계정',
    icon: ShieldCheck
  }, {
    id: 'system-logs',
    label: '시스템 로그',
    icon: ListChecks
  }, {
    id: 'service-settings',
    label: '서비스 설정',
    icon: Settings
  }]
}];
function text(value, fallback = '-') {
  const next = String(value ?? '').trim();
  return next || fallback;
}
const DEFAULT_PAYMENT_BUTTON_TEXTS = ['결제대상 추가', '결제대상추가', '결제하기', '수수료 결제', '결제요청', '확인', '결제'];
const DEFAULT_PRINT_BUTTON_TEXTS = ['저장', '발급', '출력', '인쇄'];
function numberText(value) {
  const next = Number(value || 0);
  return Number.isFinite(next) ? next.toLocaleString('ko-KR') : '0';
}
function record(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function rows(value) {
  return Array.isArray(value) ? value.map(record) : [];
}
function formatDateTime(value) {
  const raw = text(value, '');
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date).replace('AM', '오전').replace('PM', '오후');
}
function listText(value, fallback) {
  const items = Array.isArray(value) ? value.map(item => text(item, '')).filter(Boolean) : text(value, '').split(/\r?\n|,/u).map(item => item.trim()).filter(Boolean);
  return (items.length ? items : fallback).join('\n');
}
function formText(data, key, fallback = '') {
  return text(data.get(key), fallback);
}
function formNumber(data, key, fallback) {
  const parsed = Number(data.get(key));
  return Number.isFinite(parsed) ? parsed : fallback;
}
function formTextList(data, key, fallback) {
  const raw = text(data.get(key), '');
  const items = raw.split(/\r?\n|,/u).map(item => item.trim()).filter(Boolean);
  return items.length ? items : fallback;
}
function StatusPill({
  tone,
  children
}) {
  const classes = {
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    yellow: 'border-amber-200 bg-amber-50 text-amber-700',
    gray: 'border-slate-200 bg-white text-slate-600',
    red: 'border-rose-200 bg-rose-50 text-rose-700'
  }[tone];
  return <span className={`inline-flex h-6 items-center rounded-full border px-2 text-xs font-semibold ${classes}`}>{children}</span>;
}
function Panel({
  id,
  title,
  icon: Icon,
  right,
  children
}) {
  return <section id={id} className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex min-h-[56px] items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-blue-50 text-blue-700">
            <Icon className="h-4 w-4" />
          </span>
          <h2 className="text-base font-bold text-slate-950">{title}</h2>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>;
}
function EmptyState({
  label
}) {
  return <div className="grid min-h-[96px] place-items-center rounded-md border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
      {label}
    </div>;
}
function MiniMetric({
  label,
  value
}) {
  return <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-950">{value}</div>
    </div>;
}
export function RealtorAutoAdminApp({
  section,
  adminBasePath,
  appBasePath,
  apiBasePath,
  embedded = false,
  request = fetch
}) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cashgateBusy, setCashgateBusy] = useState(false);
  const [refundBusyId, setRefundBusyId] = useState('');
  const [adminNoticeDialog, setAdminNoticeDialog] = useState(EMPTY_ADMIN_NOTICE_DIALOG);
  const adminNoticeResolveRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const [cashgateBalance, setCashgateBalance] = useState(null);
  const activeSection = normalizeAdminSection(section);
  const normalizedAdminBasePath = useMemo(() => normalizePathPrefix(adminBasePath, DEFAULT_REALTORAUTO_ADMIN_BASE_PATH), [adminBasePath]);
  const normalizedAppBasePath = useMemo(() => normalizePathPrefix(appBasePath, PUBLIC_REALTORAUTO_BASE_PATH), [appBasePath]);
  const normalizedApiBasePath = useMemo(() => normalizePathPrefix(apiBasePath, DEFAULT_REALTORAUTO_API_BASE_PATH), [apiBasePath]);
  const adminGroups = useMemo(() => withAdminBasePath(realtorAutoAdminGroups, normalizedAdminBasePath), [normalizedAdminBasePath]);
  const adminApi = useCallback((path = '') => apiHref(path, normalizedApiBasePath), [normalizedApiBasePath]);
  const requestAdmin = useCallback((path = '', init) => request(adminApi(path), init), [adminApi, request]);
  const closeAdminNoticeDialog = useCallback(confirmed => {
    const resolve = adminNoticeResolveRef.current;
    adminNoticeResolveRef.current = null;
    setAdminNoticeDialog(EMPTY_ADMIN_NOTICE_DIALOG);
    resolve?.(confirmed);
  }, []);
  const confirmAdminNotice = useCallback((message, options = {}) => {
    adminNoticeResolveRef.current?.(false);
    return new Promise(resolve => {
      adminNoticeResolveRef.current = resolve;
      setAdminNoticeDialog({
        ...EMPTY_ADMIN_NOTICE_DIALOG,
        open: true,
        title: options.title || EMPTY_ADMIN_NOTICE_DIALOG.title,
        subtitle: options.subtitle || EMPTY_ADMIN_NOTICE_DIALOG.subtitle,
        message,
        tone: options.tone || 'warning',
        confirmLabel: options.confirmLabel || EMPTY_ADMIN_NOTICE_DIALOG.confirmLabel,
        cancelLabel: options.cancelLabel || '취소'
      });
    });
  }, []);
  const settings = overview?.settings ?? {};
  const session = overview?.session ?? {};
  const commonAttachments = rows(overview?.commonAttachments?.commonAttachments ?? overview?.commonAttachments?.documents);
  const irosDocuments = rows(overview?.irosPdfDocuments?.documents);
  const crawlerLogs = rows(overview?.crawlerCollectionLogs?.rows);
  const paymentRecords = rows(overview?.paymentRecords?.records);
  const auditEntries = rows(overview?.recentAuditLogs?.entries);
  const failureLogEntries = rows(overview?.failureLogs?.entries);
  const failureLogSummary = record(overview?.failureLogs?.summary);
  const queue = useMemo(() => overview?.workerQueue ?? {}, [overview?.workerQueue]);
  const irosPdfStoragePolicy = record(overview?.irosPdfDocuments?.storagePolicy);
  const paymentMacroConfig = record(settings.paymentMacroConfig);
  const paymentMacroSecretsStatus = record(settings.paymentMacroSecretsStatus);
  const paymentMacroSecretsDefaults = record(settings.paymentMacroSecretsDefaults);
  const queueMetrics = useMemo(() => [['활성 중개사', queue.activeRealtorCount], ['대기 계약방', queue.queuedContractCount], ['대기 중개사', queue.queuedRealtorCount], ['위임 계약방', queue.delegatedContractCount]], [queue]);
  const failureReasonCounts = record(failureLogSummary.reasonCounts);
  const failureMissingKeyCounts = record(failureLogSummary.missingKeyCounts);
  const failureTopMissingKeys = Object.entries(failureMissingKeyCounts).sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0)).slice(0, 8);
  const loadOverview = useCallback(async (options = {}) => {
    const requestId = ++loadRequestIdRef.current;
    if (!options.silent) setLoading(true);
    setRefreshing(true);
    setError('');
    try {
      const { ok, payload } = await fetchSectionPayload(activeSection, requestAdmin, options.force);
      if (requestId !== loadRequestIdRef.current) return;
      if (!ok || !payload.data) {
        setOverview(null);
        setError(payload.error || '관리자 정보를 불러오지 못했습니다.');
        return;
      }
      setOverview(current => sectionOverview(activeSection, payload, current));
      if (activeSection === 'dashboard') {
        setLoading(false);
        const metrics = await fetchDashboardMetrics(requestAdmin, options.force);
        if (requestId !== loadRequestIdRef.current) return;
        if (metrics.ok && metrics.payload.data) {
          setOverview(current => ({
            ...record(current),
            ...record(metrics.payload.data)
          }));
        }
      }
    } catch (loadError) {
      if (requestId !== loadRequestIdRef.current) return;
      setOverview(null);
      setError(loadError instanceof Error ? loadError.message : '관리자 정보를 불러오지 못했습니다.');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [activeSection, requestAdmin]);
  useEffect(() => {
    loadOverview();
  }, [loadOverview]);
  async function patchSettings(patch) {
    setSaving(true);
    setError('');
    try {
      const response = await requestAdmin('/admin/settings', {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patch)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.data) {
        setError(payload.error || '설정을 저장하지 못했습니다.');
        return false;
      }
      setOverview(current => current ? {
        ...current,
        settings: payload.data
      } : current);
      await loadOverview({
        silent: true
      });
      return true;
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '설정을 저장하지 못했습니다.');
      return false;
    } finally {
      setSaving(false);
    }
  }
  async function savePaymentMacroConfig(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await patchSettings({
      paymentMacroConfig: {
        registryIssueProvider: formText(data, 'registryIssueProvider', 'window-ieum-api'),
        codefServiceType: formText(data, 'codefServiceType', 'demo'),
        codefIssueType: formText(data, 'codefIssueType', '1'),
        codefOrganization: formText(data, 'codefOrganization', '0002'),
        codefOriginDataYN: formText(data, 'codefOriginDataYN', '1'),
        codefRegisterSummaryYN: formText(data, 'codefRegisterSummaryYN', '1'),
        codefWarningSkipYN: formText(data, 'codefWarningSkipYN', '0'),
        cashgateBalanceUrl: formText(data, 'cashgateBalanceUrl', 'https://minwon.cashgate.co.kr/retrieveCashInquiry.do'),
        operationMode: formText(data, 'operationMode', 'auto'),
        issuePurposeMode: formText(data, 'issuePurposeMode', 'view'),
        paymentMethodAlias: 'eCivilCash',
        paymentReplayMode: 'same-session-form-fetch',
        internalSearchTimeoutSeconds: formNumber(data, 'internalSearchTimeoutSeconds', 90),
        autoPaymentTimeoutSeconds: formNumber(data, 'autoPaymentTimeoutSeconds', 180),
        candidateSearchStartPage: formNumber(data, 'candidateSearchStartPage', 1),
        candidateSearchMaxPages: formNumber(data, 'candidateSearchMaxPages', 20),
        issueMenuFastWaitSeconds: formNumber(data, 'issueMenuFastWaitSeconds', 8),
        issueMenuSecondWaitSeconds: formNumber(data, 'issueMenuSecondWaitSeconds', 8),
        issueSearchPageReentryMaxRetries: formNumber(data, 'issueSearchPageReentryMaxRetries', 1),
        paymentTargetEntryReentryMaxAttempts: formNumber(data, 'paymentTargetEntryReentryMaxAttempts', 2),
        paymentTargetEntryProbeSeconds: formNumber(data, 'paymentTargetEntryProbeSeconds', 8),
        paymentAdvanceMaxSteps: formNumber(data, 'paymentAdvanceMaxSteps', 80),
        touchEnPrepaidSecurityGateMaxRepeats: formNumber(data, 'touchEnPrepaidSecurityGateMaxRepeats', 6),
        allowManualCheckpointOnAutoPaymentTimeout: data.get('allowManualCheckpointOnAutoPaymentTimeout') === 'on',
        paymentButtonTexts: formTextList(data, 'paymentButtonTexts', DEFAULT_PAYMENT_BUTTON_TEXTS),
        printButtonTexts: formTextList(data, 'printButtonTexts', DEFAULT_PRINT_BUTTON_TEXTS),
        nonMemberLoginMode: formText(data, 'nonMemberLoginMode', 'auto'),
        nonMemberPasswordStrategy: formText(data, 'nonMemberPasswordStrategy', 'explicit'),
        nonMemberPasswordCrawlerNumber: formText(data, 'nonMemberPasswordCrawlerNumber', ''),
        prepaidNumberPart1: formText(data, 'prepaidNumberPart1', ''),
        prepaidNumberPart2: formText(data, 'prepaidNumberPart2', ''),
        prepaidPassword: formText(data, 'prepaidPassword', ''),
        clearPrepaidCredentials: data.get('clearPrepaidCredentials') === 'on',
        nonMemberPhone: formText(data, 'nonMemberPhone', ''),
        nonMemberPassword: formText(data, 'nonMemberPassword', ''),
        clearNonMemberLogin: data.get('clearNonMemberLogin') === 'on',
        codefClientId: formText(data, 'codefClientId', ''),
        codefClientSecret: formText(data, 'codefClientSecret', ''),
        codefPublicKey: formText(data, 'codefPublicKey', ''),
        codefPhoneNo: formText(data, 'codefPhoneNo', ''),
        codefIssuePassword: formText(data, 'codefIssuePassword', ''),
        codefPrepaidNumberPart1: formText(data, 'codefPrepaidNumberPart1', ''),
        codefPrepaidNumberPart2: formText(data, 'codefPrepaidNumberPart2', ''),
        codefPrepaidPassword: formText(data, 'codefPrepaidPassword', ''),
        clearCodefCredentials: data.get('clearCodefCredentials') === 'on',
        cashgateLoginId: formText(data, 'cashgateLoginId', ''),
        cashgatePassword: formText(data, 'cashgatePassword', ''),
        clearCashgateLogin: data.get('clearCashgateLogin') === 'on'
      }
    });
  }
  async function checkCashgateBalance() {
    setCashgateBusy(true);
    setError('');
    try {
      const response = await requestAdmin('/admin/cashgate-balance', {
        method: 'POST',
        credentials: 'include'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.data) {
        setCashgateBalance(null);
        setError(payload.error || '전자민원캐쉬 잔액을 조회하지 못했습니다.');
        return;
      }
      setCashgateBalance(payload.data);
      await loadOverview({
        silent: true
      });
    } finally {
      setCashgateBusy(false);
    }
  }
  async function refundPaymentRecord(item) {
    const recordId = text(item.id, '');
    if (!recordId || refundBusyId) return;
    const amountLabel = text(item.amountText || item.amountWon, '');
    const itemName = text(item.itemName, 'realtorauto 결제');
    if (!(await confirmAdminNotice(`${itemName} ${amountLabel ? `${amountLabel} ` : ''}환불을 진행할까요?`, {
      tone: 'danger',
      confirmLabel: '환불'
    }))) {
      return;
    }
    setRefundBusyId(recordId);
    setError('');
    try {
      const response = await requestAdmin('/admin/payments', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mode: 'refund',
          paymentRecordId: recordId,
          cancelAmount: Number(item.amountWon || 0) || undefined,
          cancelReason: 'realtorauto 관리자 결제내역 화면 환불 처리'
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(payload.error || '환불 처리에 실패했습니다.');
        return;
      }
      await loadOverview({
        silent: true
      });
    } finally {
      setRefundBusyId('');
    }
  }
  const prepaidConfigured = paymentMacroSecretsStatus.prepaidConfigured === true;
  const nonMemberConfigured = paymentMacroSecretsStatus.nonMemberLoginConfigured === true;
  const codefConfigured = paymentMacroSecretsStatus.codefConfigured === true;
  const cashgateConfigured = paymentMacroSecretsStatus.cashgateLoginConfigured === true;
  const registryProviderLabel = text(paymentMacroConfig.registryIssueProvider, 'window-ieum-api') === 'codef-api' ? 'codef api' : 'window ieum api';
  const hasAdminAccess = Boolean(overview && session.role === 'ieum-admin');
  return <main id="admin-top" className={embedded ? 'min-h-0 text-slate-950' : 'min-h-screen bg-slate-50 text-slate-950'}>
      {embedded ? <section className="mb-4 flex items-center justify-between border-b border-slate-200 bg-white pb-4">
          <div>
            <h1 className="text-xl font-black text-slate-950">{SECTION_TITLES[activeSection]}</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">RealtorAuto 운영 데이터를 메뉴별로 필요한 범위만 불러옵니다.</p>
          </div>
          <button type="button" disabled={refreshing} onClick={() => loadOverview({
         force: true
       })} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </section> : <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1760px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg border border-slate-200 bg-white text-slate-900">
              <span className="text-sm font-black tracking-tight">이음</span>
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-950">이음-부동산 AUTO</h1>
              <p className="text-sm font-semibold text-slate-500">
                {text(session.profile?.officeName, 'ieum 관리자')} / {text(session.displayName || session.email, '관리자 로그인 필요')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => loadOverview()} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              새로고침
            </button>
            <a href={normalizedAppBasePath} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
              <ChevronRight className="h-4 w-4" />
              계약방 목록
            </a>
          </div>
        </div>
      </header>}

      <div className={embedded ? 'min-w-0' : 'mx-auto grid max-w-[1760px] grid-cols-[280px_minmax(0,1fr)] gap-5 px-6 py-5'}>
        {!embedded ? <aside className="sticky top-5 h-[calc(100vh-40px)] overflow-auto rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="px-2 pb-2 text-xs font-black uppercase tracking-wide text-slate-400">ADMIN MENU</div>
          <nav className="space-y-2">
            {adminGroups.map(group => {
            const GroupIcon = group.icon;
            return <div key={group.label} className="rounded-md border border-slate-100 bg-slate-50/70 p-2">
                  <div className="flex h-8 items-center gap-2 px-2 text-sm font-black text-slate-800">
                    <GroupIcon className="h-4 w-4 text-slate-500" />
                    {group.label}
                  </div>
                  <div className="mt-1 space-y-1">
                    {group.items.map(item => {
                  const ItemIcon = item.icon;
                  const active = activeSection === item.id;
                  return <a key={item.id} href={item.href || '#'} className={`flex min-h-9 items-center gap-2 rounded-md px-2 text-sm font-semibold ${active ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-white hover:text-slate-950'}`}>
                          <ItemIcon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </a>;
                })}
                  </div>
                </div>;
          })}
          </nav>
        </aside> : null}

        <div className="min-w-0 space-y-5">
          {error ? <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <XCircle className="h-4 w-4" />
              {error}
            </div> : null}

          {loading ? <div className="grid min-h-[520px] place-items-center rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-600">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                realtorauto 관리자 화면 로딩 중
              </div>
            </div> : !hasAdminAccess ? <section className="mx-auto mt-16 max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-blue-50 text-blue-700">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-black">관리자 연결을 확인해 주세요</h2>
                  <p className="text-sm font-semibold text-slate-500">현재 이음 관리자 세션으로 RealtorAuto 권한을 확인하지 못했습니다.</p>
                </div>
              </div>
              <button type="button" disabled={refreshing} onClick={() => loadOverview({ force: true })} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-black text-white hover:bg-blue-700 disabled:opacity-60">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                다시 확인
              </button>
            </section> : <>
              {activeSection === 'dashboard' ? <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-black text-slate-950">realtorauto 관리자</h2>
                      <p className="mt-1 text-sm font-semibold text-slate-500">
                        공통 설정과 운영 조회를 메뉴별 페이지에서 확인합니다. 고객용 문서는 SATA-2 공통 저장 구조 기준으로만 참조합니다.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill tone={settings.paymentMacroEnabled ? 'green' : 'gray'}>
                        결제 매크로 {settings.paymentMacroEnabled ? 'ON' : 'OFF'}
                      </StatusPill>
                      <StatusPill tone="blue">공통첨부 {numberText(commonAttachments.length)}건</StatusPill>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-3">
                    <MiniMetric label="irospdf 첨부" value={`${numberText(irosDocuments.length)}건`} />
                    <MiniMetric label="크롤러 기록" value={`${numberText(crawlerLogs.length)}건`} />
                    <MiniMetric label="워커 대기" value={`${numberText(queue.queuedContractCount)}건`} />
                    <MiniMetric label="결제 기록" value={`${numberText(paymentRecords.length)}건`} />
                  </div>
                </section> : null}

              {activeSection === 'template' ? <section id="admin-template">
                  <RealtorAutoTemplateManager
                    data={overview?.templates}
                    defaultTemplateKey={settings.defaultContractTemplateKey}
                    savingDefault={saving}
                    requestAdmin={requestAdmin}
                    onSaveDefault={key => patchSettings({ defaultContractTemplateKey: key })}
                    onTemplatesChange={nextTemplates => setOverview(current => current ? { ...current, templates: nextTemplates } : current)}
                  />
                </section> : null}

              {activeSection === 'irospdf' ? <Panel id="admin-irospdf" title="irospdf 보관소 / 첨부파일 확인" icon={Paperclip}>
                  <div className="mb-4 grid grid-cols-3 gap-3">
                    <MiniMetric label="관리자 연결 목록" value={`${numberText(irosDocuments.length)}건`} />
                    <MiniMetric label="고객 파일 저장위치" value={text(irosPdfStoragePolicy.customerFileRoot, 'aidm-sata-2')} />
                    <MiniMetric label="계약방별 복제" value={irosPdfStoragePolicy.copyPerContractRoom === true ? '사용' : '미사용'} />
                  </div>
                  <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold leading-6 text-blue-800">
                    운영 관리자 API에서 전달된 PDF 목록과 저장 상태를 표시합니다. 3016 업로드 API와 SATA-2 보관 원본 경로는 그대로 유지하며 별도 운영 로그인을 요구하지 않습니다.
                  </div>
                  <div className="mb-3 text-sm font-black text-slate-700">관리자 PDF 목록</div>
                  {irosDocuments.length ? <div className="overflow-auto">
                      <table className="w-full min-w-[820px] border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-black text-slate-500">
                            <th className="px-3 py-2">계약방</th>
                            <th className="px-3 py-2">파일명</th>
                            <th className="px-3 py-2">상태</th>
                            <th className="px-3 py-2">저장위치</th>
                            <th className="px-3 py-2">수정일</th>
                          </tr>
                        </thead>
                        <tbody>
                          {irosDocuments.map(item => {
                    const contract = record(item.contract);
                    const fileHref = realtorAutoFileHref(item);
                    return <tr key={text(item.id)} className="border-b border-slate-100">
                                <td className="px-3 py-2 font-bold text-slate-900">{text(contract.title || contract.displayAddress)}</td>
                                <td className="px-3 py-2 text-slate-700">
                                  {fileHref ? <a
                                    href={fileHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-irospdf-file-link={text(item.id)}
                                    className="inline-flex max-w-full items-center gap-1.5 font-bold text-blue-700 underline decoration-blue-200 underline-offset-2 hover:text-blue-900"
                                    title="PDF 원문 열기"
                                  >
                                    <span className="truncate">{text(item.fileName || item.displayName)}</span>
                                    <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                  </a> : <span>{text(item.fileName || item.displayName)}</span>}
                                </td>
                                <td className="px-3 py-2">
                                  <StatusPill tone={text(item.status).toUpperCase() === 'ISSUED' ? 'green' : 'yellow'}>{text(item.status)}</StatusPill>
                                </td>
                                <td className="px-3 py-2 text-slate-600">{text(item.storageRootPolicy, 'aidm-sata-2')}</td>
                                <td className="px-3 py-2 text-slate-500">{formatDateTime(item.updatedAt || item.createdAt)}</td>
                              </tr>;
                  })}
                        </tbody>
                      </table>
                    </div> : <EmptyState label="표시할 irospdf/등기부 첨부파일이 없습니다." />}
                </Panel> : null}

              {activeSection === 'payment-macro' ? <Panel id="admin-payment-macro" title="결제 매크로 설정" icon={settings.paymentMacroEnabled ? ToggleRight : ToggleLeft} right={<button type="button" disabled={saving} onClick={() => patchSettings({
            paymentMacroEnabled: !settings.paymentMacroEnabled
          })} className={`inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-black text-white ${settings.paymentMacroEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-500 hover:bg-slate-600'} disabled:opacity-60`}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : settings.paymentMacroEnabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      {settings.paymentMacroEnabled ? '자동 실행 사용' : '자동 실행 중지'}
                    </button>}>
                  <div className="grid grid-cols-5 gap-3">
                    <MiniMetric label="현재 상태" value={settings.paymentMacroEnabled ? '자동 실행' : '수동 대기'} />
                    <MiniMetric label="발급 API" value={registryProviderLabel} />
                    <MiniMetric label="결제수단" value={text(paymentMacroConfig.paymentMethodAlias, 'eCivilCash')} />
                    <MiniMetric label="eCivilCash" value={prepaidConfigured ? `설정됨 · 끝 ${text(paymentMacroSecretsStatus.prepaidNumberLast2, '**')}` : '미설정'} />
                    <MiniMetric label="CODEF" value={codefConfigured ? '설정됨' : '미설정'} />
                    <MiniMetric label="비회원 계정" value={nonMemberConfigured ? `설정됨 · 끝 ${text(paymentMacroSecretsStatus.nonMemberPhoneLast4, '****')}` : '미설정'} />
                  </div>

                  <form className="mt-5 space-y-5" onSubmit={savePaymentMacroConfig}>
                    <section className="rounded-md border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-black text-slate-900">등기부 발급 API / 매크로 요구값</h3>
                          <p className="mt-1 text-xs font-semibold text-slate-500">
                            원본 admin에 붙여도 동작하도록 현재 페이지 경로가 아니라 관리자 설정 저장소 기준으로만 읽습니다.
                          </p>
                        </div>
                        <StatusPill tone={cashgateConfigured ? 'green' : 'yellow'}>
                          전자민원캐쉬 {cashgateConfigured ? '로그인 설정됨' : '확인 필요'}
                        </StatusPill>
                      </div>
                      <div className="mt-3 grid grid-cols-4 gap-3">
                        <label className="text-xs font-bold text-slate-600">
                          API 종류
                          <select name="registryIssueProvider" defaultValue={text(paymentMacroConfig.registryIssueProvider, 'window-ieum-api')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                            <option value="window-ieum-api">window ieum api</option>
                            <option value="codef-api">codef api</option>
                          </select>
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          CODEF 환경
                          <select name="codefServiceType" defaultValue={text(paymentMacroConfig.codefServiceType, 'demo')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                            <option value="demo">데모</option>
                            <option value="sandbox">샌드박스</option>
                            <option value="api">운영</option>
                          </select>
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          CODEF 요청
                          <select name="codefIssueType" defaultValue={text(paymentMacroConfig.codefIssueType, '1')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                            <option value="1">열람</option>
                            <option value="0">발급</option>
                          </select>
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          기관코드
                          <input name="codefOrganization" defaultValue={text(paymentMacroConfig.codefOrganization, '0002')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          originDataYN
                          <input name="codefOriginDataYN" defaultValue={text(paymentMacroConfig.codefOriginDataYN, '1')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          registerSummaryYN
                          <input name="codefRegisterSummaryYN" defaultValue={text(paymentMacroConfig.codefRegisterSummaryYN, '1')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          warningSkipYN
                          <input name="codefWarningSkipYN" defaultValue={text(paymentMacroConfig.codefWarningSkipYN, '0')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          전자민원캐쉬 잔액 URL
                          <input name="cashgateBalanceUrl" defaultValue={text(paymentMacroConfig.cashgateBalanceUrl, 'https://minwon.cashgate.co.kr/retrieveCashInquiry.do')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                        </label>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-4">
                        <div className="rounded-md border border-blue-100 bg-blue-50/60 p-3">
                          <h4 className="text-xs font-black text-blue-900">CODEF API 값</h4>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <label className="text-xs font-bold text-slate-600">
                              client id
                              <input name="codefClientId" defaultValue={text(paymentMacroSecretsDefaults.codefClientId, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              client secret
                              <input name="codefClientSecret" type="password" defaultValue={text(paymentMacroSecretsDefaults.codefClientSecret, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="col-span-2 text-xs font-bold text-slate-600">
                              public key
                              <textarea name="codefPublicKey" rows={3} defaultValue={text(paymentMacroSecretsDefaults.codefPublicKey, '')} className="mt-1 w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              신청자 전화번호
                              <input name="codefPhoneNo" defaultValue={text(paymentMacroSecretsDefaults.codefPhoneNo, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              IROS 비밀번호
                              <input name="codefIssuePassword" type="password" defaultValue={text(paymentMacroSecretsDefaults.codefIssuePassword, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              ePrepay 번호 1
                              <input name="codefPrepaidNumberPart1" defaultValue={text(paymentMacroSecretsDefaults.codefPrepaidNumberPart1, text(paymentMacroSecretsDefaults.prepaidNumberPart1, ''))} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              ePrepay 번호 2
                              <input name="codefPrepaidNumberPart2" defaultValue={text(paymentMacroSecretsDefaults.codefPrepaidNumberPart2, text(paymentMacroSecretsDefaults.prepaidNumberPart2, ''))} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              ePrepay 비밀번호
                              <input name="codefPrepaidPassword" type="password" defaultValue={text(paymentMacroSecretsDefaults.codefPrepaidPassword, text(paymentMacroSecretsDefaults.prepaidPassword, ''))} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="flex items-center gap-2 rounded-md border border-blue-100 bg-white px-3 py-2 text-xs font-bold text-slate-700">
                              <input name="clearCodefCredentials" type="checkbox" />
                              CODEF 값 삭제
                            </label>
                          </div>
                        </div>

                        <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="text-xs font-black text-emerald-900">전자민원캐쉬 잔액 조회</h4>
                            <button type="button" onClick={checkCashgateBalance} disabled={cashgateBusy} className="inline-flex h-8 items-center gap-2 rounded-md bg-emerald-600 px-3 text-xs font-black text-white hover:bg-emerald-700 disabled:opacity-60">
                              {cashgateBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                              잔액 확인
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <label className="text-xs font-bold text-slate-600">
                              로그인 ID
                              <input name="cashgateLoginId" defaultValue={text(paymentMacroSecretsDefaults.cashgateLoginId, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              로그인 비밀번호
                              <input name="cashgatePassword" type="password" defaultValue={text(paymentMacroSecretsDefaults.cashgatePassword, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="flex items-center gap-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-xs font-bold text-slate-700">
                              <input name="clearCashgateLogin" type="checkbox" />
                              전자민원캐쉬 로그인값 삭제
                            </label>
                            <div className="rounded-md border border-emerald-100 bg-white px-3 py-2">
                              <div className="text-xs font-bold text-slate-500">조회 잔액</div>
                              <div className="mt-1 text-base font-black text-emerald-700">
                                {cashgateBalance?.balanceText ? text(cashgateBalance.balanceText) : '조회 전'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="rounded-md border border-slate-200 p-4">
                      <h3 className="text-sm font-black text-slate-900">실행 기준</h3>
                      <div className="mt-3 grid grid-cols-4 gap-3">
                        <label className="text-xs font-bold text-slate-600">
                          진행 방식
                          <select name="operationMode" defaultValue={text(paymentMacroConfig.operationMode, 'auto')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                            <option value="auto">자동 결제</option>
                            <option value="manual">수동 확인 후 진행</option>
                          </select>
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          발급 목적
                          <select name="issuePurposeMode" defaultValue={text(paymentMacroConfig.issuePurposeMode, 'view')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                            <option value="view">열람</option>
                            <option value="print">발급/출력</option>
                            <option value="electronic">전자문서</option>
                          </select>
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          결제 방식
                          <input value="eCivilCash 선불전자지급수단" readOnly className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-700" />
                        </label>
                        <label className="text-xs font-bold text-slate-600">
                          결제 재현 방식
                          <input value="동일 세션 form 재사용" readOnly className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-700" />
                        </label>
                      </div>
                    </section>

                    <section className="rounded-md border border-slate-200 p-4">
                      <h3 className="text-sm font-black text-slate-900">대기 시간 / 감지 기준</h3>
                      <div className="mt-3 grid grid-cols-4 gap-3">
                        {[['internalSearchTimeoutSeconds', '주소 검색 대기초', 90], ['autoPaymentTimeoutSeconds', '자동결제 대기초', 180], ['candidateSearchStartPage', '검색 시작 페이지', 1], ['candidateSearchMaxPages', '검색 최대 페이지', 20], ['issueMenuFastWaitSeconds', '발급 메뉴 1차 대기초', 8], ['issueMenuSecondWaitSeconds', '발급 메뉴 2차 대기초', 8], ['issueSearchPageReentryMaxRetries', '검색 재진입 재시도', 1], ['paymentTargetEntryReentryMaxAttempts', '결제대상 재진입', 2], ['paymentTargetEntryProbeSeconds', '결제대상 감지초', 8], ['paymentAdvanceMaxSteps', '결제 단계 최대횟수', 80], ['touchEnPrepaidSecurityGateMaxRepeats', '보안 게이트 반복', 6]].map(([name, label, fallback]) => <label key={String(name)} className="text-xs font-bold text-slate-600">
                            {String(label)}
                            <input name={String(name)} type="number" min={0} defaultValue={Number(paymentMacroConfig[String(name)] ?? fallback)} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                          </label>)}
                        <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700">
                          <input name="allowManualCheckpointOnAutoPaymentTimeout" type="checkbox" defaultChecked={paymentMacroConfig.allowManualCheckpointOnAutoPaymentTimeout === true} />
                          자동결제 지연 시 수동 확인 허용
                        </label>
                      </div>
                    </section>

                    <section className="grid grid-cols-2 gap-4">
                      <div className="rounded-md border border-slate-200 p-4">
                        <h3 className="text-sm font-black text-slate-900">버튼 감지 문구</h3>
                        <label className="mt-3 block text-xs font-bold text-slate-600">
                          결제 진행 버튼명
                          <textarea name="paymentButtonTexts" rows={7} defaultValue={listText(paymentMacroConfig.paymentButtonTexts, DEFAULT_PAYMENT_BUTTON_TEXTS)} className="mt-1 w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900" />
                        </label>
                        <label className="mt-3 block text-xs font-bold text-slate-600">
                          저장/출력 버튼명
                          <textarea name="printButtonTexts" rows={4} defaultValue={listText(paymentMacroConfig.printButtonTexts, DEFAULT_PRINT_BUTTON_TEXTS)} className="mt-1 w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900" />
                        </label>
                      </div>

                      <div className="space-y-4">
                        <div className="rounded-md border border-slate-200 p-4">
                          <h3 className="text-sm font-black text-slate-900">비회원 입력 로직</h3>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <label className="text-xs font-bold text-slate-600">
                              비회원 계정 입력
                              <select name="nonMemberLoginMode" defaultValue={text(paymentMacroConfig.nonMemberLoginMode, 'auto')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                                <option value="auto">자동 입력</option>
                                <option value="disabled">사용 안 함</option>
                              </select>
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              비밀번호 결정
                              <select name="nonMemberPasswordStrategy" defaultValue={text(paymentMacroConfig.nonMemberPasswordStrategy, 'explicit')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900">
                                <option value="explicit">관리자 입력값</option>
                                <option value="crawler-prefix">크롤러 번호 기반</option>
                              </select>
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              크롤러 번호
                              <input name="nonMemberPasswordCrawlerNumber" defaultValue={text(paymentMacroConfig.nonMemberPasswordCrawlerNumber, '')} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              비회원 전화번호
                              <input name="nonMemberPhone" defaultValue={text(paymentMacroSecretsDefaults.nonMemberPhone, '')} placeholder={nonMemberConfigured ? `저장됨 · 끝 ${text(paymentMacroSecretsStatus.nonMemberPhoneLast4, '****')}` : '새 번호 입력'} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              비회원 비밀번호
                              <input name="nonMemberPassword" type="password" defaultValue={text(paymentMacroSecretsDefaults.nonMemberPassword, '')} placeholder={paymentMacroSecretsStatus.nonMemberPasswordProvided ? '저장됨 · 변경할 때만 입력' : '새 비밀번호 입력'} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700">
                              <input name="clearNonMemberLogin" type="checkbox" />
                              비회원 계정 삭제
                            </label>
                          </div>
                        </div>

                        <div className="rounded-md border border-slate-200 p-4">
                          <h3 className="text-sm font-black text-slate-900">eCivilCash 보관값</h3>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <label className="text-xs font-bold text-slate-600">
                              번호 1
                              <input name="prepaidNumberPart1" defaultValue={text(paymentMacroSecretsDefaults.prepaidNumberPart1, '')} placeholder={prepaidConfigured ? '저장됨' : '새 번호 입력'} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              번호 2
                              <input name="prepaidNumberPart2" defaultValue={text(paymentMacroSecretsDefaults.prepaidNumberPart2, '')} placeholder={prepaidConfigured ? `저장됨 · 끝 ${text(paymentMacroSecretsStatus.prepaidNumberLast2, '**')}` : '새 번호 입력'} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="text-xs font-bold text-slate-600">
                              결제 비밀번호
                              <input name="prepaidPassword" type="password" defaultValue={text(paymentMacroSecretsDefaults.prepaidPassword, '')} placeholder={paymentMacroSecretsStatus.prepaidPasswordProvided ? '저장됨 · 변경할 때만 입력' : '새 비밀번호 입력'} className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm font-bold text-slate-900" />
                            </label>
                            <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700">
                              <input name="clearPrepaidCredentials" type="checkbox" />
                              eCivilCash 보관값 삭제
                            </label>
                          </div>
                        </div>
                      </div>
                    </section>

                    <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                      <div className="text-xs font-semibold text-slate-500">
                        마지막 변경: {text(settings.paymentMacroUpdatedBy || settings.updatedBy)} · {formatDateTime(settings.paymentMacroUpdatedAt || settings.updatedAt)}
                      </div>
                      <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-black text-white hover:bg-blue-700 disabled:opacity-60">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
                        결제 매크로 설정 저장
                      </button>
                    </div>
                  </form>
                </Panel> : null}

              {activeSection === 'crawler' ? <Panel id="admin-crawler" title="크롤러 수집내역 확인" icon={Database}>
                  {crawlerLogs.length ? <div className="overflow-auto">
                      <table className="w-full min-w-[860px] border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-black text-slate-500">
                            <th className="px-3 py-2">계약방</th>
                            <th className="px-3 py-2">제공자</th>
                            <th className="px-3 py-2">상태</th>
                            <th className="px-3 py-2">행수</th>
                            <th className="px-3 py-2">PNU</th>
                            <th className="px-3 py-2">수집일</th>
                          </tr>
                        </thead>
                        <tbody>
                          {crawlerLogs.map(item => {
                    const contract = record(item.contract);
                    return <tr key={text(item.id)} className="border-b border-slate-100">
                                <td className="px-3 py-2 font-bold text-slate-900">{text(contract.title || contract.displayAddress)}</td>
                                <td className="px-3 py-2 text-slate-700">{text(item.provider)}</td>
                                <td className="px-3 py-2">
                                  <StatusPill tone={text(item.status, '').toUpperCase().includes('ERROR') ? 'red' : 'blue'}>{text(item.status, '기록됨')}</StatusPill>
                                </td>
                                <td className="px-3 py-2 text-slate-700">{numberText(item.rowCount)} / {numberText(item.totalCount)}</td>
                                <td className="px-3 py-2 text-slate-500">{text(item.pnu)}</td>
                                <td className="px-3 py-2 text-slate-500">{formatDateTime(item.updatedAt)}</td>
                              </tr>;
                  })}
                        </tbody>
                      </table>
                    </div> : <EmptyState label="수집 기록이 아직 없습니다." />}
                </Panel> : null}

              {activeSection === 'worker-queue' ? <Panel id="admin-worker" title="워커 대기 현황 및 Q 대기 실시간 열람" icon={Activity}>
                  <div className="grid grid-cols-4 gap-3">
                    {queueMetrics.map(([label, value]) => <MiniMetric key={String(label)} label={String(label)} value={numberText(value)} />)}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="mb-2 text-sm font-black text-slate-800">대기 중개사</h3>
                      {rows(queue.queuedByRealtor).length ? <div className="max-h-56 overflow-auto rounded-md border border-slate-200">
                          {rows(queue.queuedByRealtor).map((item, index) => <div key={`${text(item.realtorId)}-${index}`} className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                              <span className="font-bold text-slate-700">{text(item.realtorId)}</span>
                              <StatusPill tone="yellow">{numberText(item.count)}건</StatusPill>
                            </div>)}
                        </div> : <EmptyState label="대기 중인 Q가 없습니다." />}
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-black text-slate-800">최근 관리자 로그</h3>
                      {auditEntries.length ? <div className="max-h-56 overflow-auto rounded-md border border-slate-200">
                          {auditEntries.slice(0, 8).map(item => <div key={text(item.id)} className="border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-bold text-slate-800">{text(item.action)}</span>
                                <span className="text-xs font-semibold text-slate-400">{formatDateTime(item.createdAt)}</span>
                              </div>
                              <div className="mt-1 truncate text-xs font-semibold text-slate-500">{text(item.summary)}</div>
                            </div>)}
                        </div> : <EmptyState label="표시할 로그가 없습니다." />}
                    </div>
                  </div>
                </Panel> : null}

              {activeSection === 'failure-log' ? <Panel id="admin-failure-log" title="실패log" icon={XCircle}>
                  <div className="grid grid-cols-4 gap-3">
                    <MiniMetric label="최근 실패" value={`${numberText(failureLogEntries.length)}건`} />
                    <MiniMetric label="사전분류" value={`${numberText(failureLogSummary.preflightCount)}건`} />
                    <MiniMetric label="공부서류값" value={`${numberText(failureLogSummary.propertyScopeCount)}건`} />
                    <MiniMetric label="분석기간" value={`${numberText(failureLogSummary.days)}일`} />
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <h3 className="mb-2 text-sm font-black text-slate-800">원인코드</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(failureReasonCounts).length ? Object.entries(failureReasonCounts).map(([key, value]) => <StatusPill key={key} tone="yellow">
                              {key} {numberText(value)}
                            </StatusPill>) : <span className="text-sm font-semibold text-slate-500">집계된 원인코드가 없습니다.</span>}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <h3 className="mb-2 text-sm font-black text-slate-800">미수집 필드</h3>
                      <div className="flex flex-wrap gap-2">
                        {failureTopMissingKeys.length ? failureTopMissingKeys.map(([key, value]) => <StatusPill key={key} tone="blue">
                              {key} {numberText(value)}
                            </StatusPill>) : <span className="text-sm font-semibold text-slate-500">미수집 필드가 없습니다.</span>}
                      </div>
                    </div>
                  </div>
                  {failureLogEntries.length ? <div className="mt-4 overflow-auto">
                      <table className="w-full min-w-[1040px] border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-black text-slate-500">
                            <th className="px-3 py-2">시간</th>
                            <th className="px-3 py-2">유형</th>
                            <th className="px-3 py-2">계약방</th>
                            <th className="px-3 py-2">상태</th>
                            <th className="px-3 py-2">원인</th>
                            <th className="px-3 py-2">미수집값</th>
                            <th className="px-3 py-2">요약</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failureLogEntries.map(item => {
                    const missingKeys = Array.isArray(item.missingKeys) ? item.missingKeys.map(value => text(value)).filter(Boolean) : [];
                    return <tr key={text(item.id)} className="border-b border-slate-100 align-top">
                                <td className="px-3 py-2 font-semibold text-slate-500">{formatDateTime(item.createdAt)}</td>
                                <td className="px-3 py-2 font-bold text-slate-800">{text(item.logType)}</td>
                                <td className="px-3 py-2 font-semibold text-slate-600">{text(item.contractId)}</td>
                                <td className="px-3 py-2">
                                  <StatusPill tone={text(item.status).includes('MISSING') || text(item.status).includes('FAILED') ? 'red' : 'yellow'}>
                                    {text(item.status)}
                                  </StatusPill>
                                </td>
                                <td className="px-3 py-2 font-semibold text-slate-700">{text(item.reasonCode)}</td>
                                <td className="px-3 py-2 text-xs font-semibold text-slate-600">{missingKeys.length ? missingKeys.join(', ') : '-'}</td>
                                <td className="px-3 py-2 text-xs font-semibold text-slate-500">{text(item.summary)}</td>
                              </tr>;
                  })}
                        </tbody>
                      </table>
                    </div> : <div className="mt-4">
                      <EmptyState label="최근 실패log가 없습니다." />
                    </div>}
                </Panel> : null}

              {activeSection === 'payments' ? <Panel id="admin-payments" title="realtorauto 결제내역" icon={CreditCard}>
                  {paymentRecords.length ? <div className="overflow-auto">
                      <table className="w-full min-w-[1120px] border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-black text-slate-500">
                            <th className="px-3 py-2">일시</th>
                            <th className="px-3 py-2">결제자</th>
                            <th className="px-3 py-2">항목</th>
                            <th className="px-3 py-2">금액</th>
                            <th className="px-3 py-2">결제수단</th>
                            <th className="px-3 py-2">상태</th>
                            <th className="px-3 py-2">주문</th>
                            <th className="px-3 py-2">환불</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentRecords.map(item => {
                    const contract = record(item.contract);
                    const refundStatus = text(item.refundStatus, '');
                    const alreadyRefunded = /cancel|refund|환불/u.test(refundStatus.toLowerCase());
                    const statusText = text(item.status, '기록됨');
                    const statusTone = alreadyRefunded ? 'green' : /fail|error|실패|오류/u.test(statusText.toLowerCase()) ? 'red' : /paid|success|done|complete|완료/u.test(statusText.toLowerCase()) ? 'green' : 'blue';
                    const refundable = item.refundable === true && !alreadyRefunded;
                    const busy = refundBusyId === text(item.id, '');
                    return <tr key={text(item.id)} className="border-b border-slate-100 align-top">
                                <td className="px-3 py-2 font-semibold text-slate-600">
                                  {formatDateTime(item.paidAt || item.createdAt || item.updatedAt)}
                                  <div className="mt-1 text-xs text-slate-400">수정 {formatDateTime(item.updatedAt)}</div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-bold text-slate-900">{text(item.payerName, '확인 필요')}</div>
                                  <div className="mt-1 text-xs font-semibold text-slate-500">{text(item.payerContact || contract.id, '')}</div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-bold text-slate-900">{text(item.itemName, 'realtorauto 결제')}</div>
                                  <div className="mt-1 max-w-[280px] truncate text-xs font-semibold text-slate-500">
                                    {text(contract.title || contract.displayAddress)}
                                  </div>
                                </td>
                                <td className="px-3 py-2 font-black text-slate-900">{text(item.amountText || item.amountWon)}</td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-slate-700">{text(item.paymentMethodLabel, '확인 필요')}</div>
                                  <div className="mt-1 text-xs text-slate-400">{text(item.paymentProvider || item.routeLabel, '')}</div>
                                </td>
                                <td className="px-3 py-2">
                                  <StatusPill tone={statusTone}>{statusText}</StatusPill>
                                  {refundStatus ? <div className="mt-1 text-xs font-semibold text-emerald-700">환불 {refundStatus}</div> : null}
                                </td>
                                <td className="px-3 py-2 text-xs font-semibold text-slate-500">
                                  <div>{text(item.orderId || item.paymentId)}</div>
                                  <div className="mt-1 text-slate-400">{text(item.sourceType, '')}</div>
                                </td>
                                <td className="px-3 py-2">
                                  <button type="button" onClick={() => refundPaymentRecord(item)} disabled={!refundable || busy} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-black text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                    {alreadyRefunded ? '환불완료' : refundable ? '환불' : '환불불가'}
                                  </button>
                                  {text(item.refundError, '') ? <div className="mt-1 text-xs font-semibold text-rose-600">{text(item.refundError)}</div> : null}
                                </td>
                              </tr>;
                  })}
                        </tbody>
                      </table>
                    </div> : <EmptyState label="realtorauto 결제내역이 없습니다." />}
                </Panel> : null}
            </>}
        </div>
      </div>
      <RealtorAutoCommonNoticeDialog open={adminNoticeDialog.open} logoSrc={`${normalizedAppBasePath}/logo-ieum-header.png`} title={adminNoticeDialog.title} subtitle={adminNoticeDialog.subtitle} message={adminNoticeDialog.message} tone={adminNoticeDialog.tone} confirmLabel={adminNoticeDialog.confirmLabel} cancelLabel={adminNoticeDialog.cancelLabel || undefined} onConfirm={() => closeAdminNoticeDialog(true)} onCancel={adminNoticeDialog.cancelLabel ? () => closeAdminNoticeDialog(false) : undefined} />
    </main>;
}
